import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FolderOpenOutlined } from "@ant-design/icons";
import Notiflix from "notiflix";

// PROJECT IMPORTS
import { BOM } from "../../../../../../services/definedMethod";
import cnt from "../../../../../../services/definedConstant";

import {
  Table,
  Tooltip,
  Button,
  Form,
  Spin,
  Row,
  Col,
  Input,
  Modal,
  Divider,
  Space,
  Typography,
} from "antd";

import {
  apiConfig,
  labelConfig,
  staticConfig,
} from "../../../../customer.config";

import styles from "../../../../../../assets/styles/styles";
import { isDebit } from "../../../../../../components/facilities/AccountFacility";
import DownloadTableButtons from "../../../../../../components/DownloadTableButtons";
import { showCheckerApprovalModal } from "../../../../../../components/CheckerModal";
import CustomPagination from "../../../../../../components/CustomPagination";

const dateFormat = "YYYY-MM-DD";

const Transactions = (props) => {
  const { Text } = Typography;

  const {
    state,
    userData,
    transactionsList,
    currentPage,
    tableCount,
    totalItems,
    setCurrentPage,
    onRefresh,
  } = props;

  const { session } = userData;

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const enhancedList = transactionsList.map((item) => ({
    ...item,
    debitValue: isDebit(item.transactionTypeDetail) ? item.amount : "-",
    creditValue: !isDebit(item.transactionTypeDetail) ? item.amount : "-",
  }));

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
    },
    {
      title: "Transaction Date",
      dataIndex: "transactionDate",
    },
    {
      title: "Transaction Type",
      dataIndex: "transactionType",
    },
    {
      title: "Debit",
      dataIndex: "debitValue",
      render: (_, record) => {
        const val = isDebit(record.transactionTypeDetail)
          ? record.amount
          : "-";

        if (val === "-") return "-";

        return `${Number(val).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      },
    },
    {
      title: "Credit",
      dataIndex: "creditValue",
      render: (_, record) => {
        const val = !isDebit(record.transactionTypeDetail)
          ? record.amount
          : "-";

        if (val === "-") return "-";

        return `${Number(val).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      },
    },
    {
      title: "Balance",
      dataIndex: "balance",
      render: (_, record) => {
        if (!record?.balance || record.balance === "-") return "-";

        const [code, rawAmount] = record.balance.split(" ");
        const amount = Number(rawAmount);

        if (isNaN(amount)) return record.balance;

        return `${code} ${amount.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      },
    },
    {
      title: "View Receipt",
      dataIndex: "viewReceipt",
    },
    {
      title: "Actions",
      render: (_, record) => (
        <DetailTransactions
          currentCustomerObj={state}
          userData={userData}
          transactionObj={record}
        />
      ),
    },
  ];

  function handleViewJournalEntry() {
    navigate("/savings-journal-entries", { state });
  }

  return (
    <>
      <Spin spinning={isLoading} size="large">
        <Row>
          <Button
            type="primary"
            className="of-primaryBtnColor"
            shape="round"
            onClick={handleViewJournalEntry}
          >
            <FolderOpenOutlined />
            &nbsp;{labelConfig.viewJournalEntries}
          </Button>

          <DownloadTableButtons
            downloadData={enhancedList}
            columns={columns}
            fileName={"Savings Transaction List"}
          />
        </Row>

        <Divider />

        <Table
          pagination={false}
          columns={columns}
          dataSource={enhancedList}
        />

        <CustomPagination
          totalItems={totalItems}
          pageSize={Number(tableCount)}
          onPageChange={(page, offset) => onRefresh(offset)}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </Spin>
    </>
  );
};

export default Transactions;

const DetailTransactions = (props) => {
  const navigate = useNavigate();

  const { currentCustomerObj, userData, transactionObj } = props;
  const { session } = userData;

  const [useForm] = Form.useForm();
  const [inputField, setInputField] = useState([]);
  const [transactionsDetail, setTransactionsDetail] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);


  const getDetailById = () => {
    setIsLoading(true);

    const url = `${apiConfig.savingsAccounts}/${currentCustomerObj.id}/transactions/${transactionObj.id}`;
    const body = { session };

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err?.errors?.[0]?.defaultUserMessage);
        setIsLoading(false);
        return;
      }

      const objDetail = res;
      setTransactionsDetail(objDetail);

      setInputField([
        {
          name: labelConfig.transactionId,
          value: objDetail?.id,
          type: "general",
        },
        {
          name: labelConfig.transactionType,
          value: objDetail?.transactionType?.value,
          type: "general",
        },
        {
          name: labelConfig.date,
          value: BOM.FormatArrayDate(objDetail?.date),
          type: "general",
        },
        {
          name: labelConfig.currency,
          value: objDetail?.currency?.displayLabel,
          type: "general",
        },
        {
          name: labelConfig.amount,
          value: objDetail.amount,
          type: "general",
        },
        {
          name: labelConfig.lienTransaction,
          value: objDetail?.lienTransaction ? "True" : "False",
          type: "general",
        },
        {
          name: labelConfig.totalWithdrawalFees,
          value: objDetail?.summary?.totalWithdrawalFees,
          type: "general",
        },
      ]);

      setIsLoading(false);
    });
  };

  const showModal = () => {
    setIsModalOpen(true);
    getDetailById();
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const onUndoTransaction = (txnDetail) => {
    const formValues = {
      transactionAmount: txnDetail?.amount,
      transactionDate: BOM.DateTime(txnDetail.date, "only-date"),
      dateFormat: "dd MMMM yyyy",
    };

    Notiflix.Confirm.show(
      "Are you sure?",
      "Reverse Transaction",
      "Yes",
      "No",
      () => {
        const params = { command: "undo" };
        const url = `${apiConfig.savingsAccounts}/${currentCustomerObj.id}/transactions/${transactionObj.id}`;

        const body = {
          method: "POST",
          locale: "en",
          ...formValues,
          session,
          params,
        };

        setIsLoading(true);

        BOM.FetchReqAction(
          body,
          url,
          (err, res) => {
            if (err) {
              BOM.AlertMsg(cnt.DANGER, err?.errors?.[0]?.defaultUserMessage);
              setIsLoading(false);
            } else {
              BOM.AlertMsg(cnt.SUCCESS, labelConfig.undoTransactionMsg);
              setIsModalOpen(false);
              setIsLoading(false);
            }
          },
          {
            navigate,
            showCheckerModal: showCheckerApprovalModal,
            onCheckerApproval: () => {
              setIsModalOpen(false);
              setIsLoading(false);
            },
          }
        );
      }
    );
  };

  return (
    <>
      <Tooltip title="View Detail">
        <Button
          type="primary"
          icon={<FolderOpenOutlined />}
          onClick={showModal}
          shape="round"
          size="small"
          className="of-primaryBtnColor"
        />
      </Tooltip>

      <Modal
        title="Transaction Detail"
        centered
        open={isModalOpen}
        onCancel={handleCancel}
        width={800}
        okButtonProps={{
          style: { ...styles.ofPrimaryBtnColor },
        }}
        footer={null}
        style={{ height: 600, overflow: "auto" }}
      >
        <Spin spinning={isLoading} tip="Loading..." size="large">
          <Form layout="vertical" form={useForm}>
            <div style={{ textAlign: "right" }}>
              <Space size="small">
                {BOM.HasPermission("UNDOTRANSACTION_SAVINGSACCOUNT") && (
                  <Button
                    type="primary"
                    shape="round"
                    className="of-primaryBtnColor"
                    danger
                    onClick={() => onUndoTransaction(transactionsDetail)}
                  >
                    Reverse
                  </Button>
                )}
              </Space>
            </div>

            <Row>
              {inputField.map(
                (item, index) =>
                  item?.type?.toLowerCase() === "general" && (
                    <Col key={index} span={8} style={{ padding: "0px 8px" }}>
                      <Form.Item label={item.name}>
                        <Input
                          disabled
                          value={item.value}
                          className="of-bold-disable-input"
                        />
                      </Form.Item>
                    </Col>
                  )
              )}
            </Row>
          </Form>
        </Spin>
      </Modal>
    </>
  );
};
